import React, { FormEvent, useState } from 'react';
import Prepayment from '../../../models/Prepayment';
import Pagination from '../../../models/Pagination';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, TableContainer, Stack, Tooltip, Collapse } from '@mui/material';
import PaginationButtons from '../../PaginationButtons';
import { formattedCurrency } from '../../../utils/StringUtils';
import moment from 'moment';
import { ISTableTitle, ISTableHeaderCell, ISTableRow, ISTableCell } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import UsePrepayment from '../../../models/UsePrepayment';
import { preorderStatusToDescription } from '../../Utils/CommonConst';
import { useThemeContext } from '../../../theme/ThemeContext';
import { ArrowDropDownOutlined, ArrowDropUpOutlined } from '@mui/icons-material';
import { statusOrder } from '../Orders';

export const prepaymentStatus = ['ACTIVO', 'INACTIVO', 'FINALIZADO']

export type PrepaymentStatus = typeof prepaymentStatus[number]

export const prepaymentStatusToDescription = (type: PrepaymentStatus): string => {
  switch (type) {
    case 'ACTIVO': return "active"
    case 'INACTIVO': return "inactive"
    case 'FINALIZADO': return "finalized"
  }
  return "not"
}


export interface Row {
  data: Prepayment
  action?: string; // = 'create' | 'update' | 'disabled'  | 'error' ;
  user?: string;
  statusPO?: string;
}

interface Props {
  rows: Row[];
  loading: boolean;
  pagination?: Pagination;
  fullInfo?: boolean;
  adminInfo?: boolean;
  showInPreorder?: boolean;
  toPreorder?: boolean;
  onChangePage?: (page: number) => void;
  onClickDetail?: (prepaymentId: string) => void;
}

const Prepayments: React.FC<Props> = (props) => {
  const { t } = useTranslation(['prepayment', 'preorder'])
  const { theme } = useThemeContext()
  const [showFullInfo, setShowFullInfo] = useState<boolean>(!props.adminInfo)

  const mapActionToColor = (action?: string) => {
    switch (action) {
      case 'create': return 'green';
      case 'update': return 'yellow';
      case 'error': return 'red';
      case 'disabled': return 'red';
      default: return 'default';
    }
  }

  const rowsToShow = /* !props.fullInfo && !props.toPreorder ? props.rows.slice(0, 5) : */ props.rows

  return (
    <Stack
      style={{ display: 'flex', flexDirection: 'column', height: props.fullInfo ? !props.adminInfo ? 'calc(100% - var(--header-height)' : '' : '100%', borderRadius: "10px", margin: "10px", maxWidth: props.toPreorder ? "1100px" : !props.fullInfo ? "770px" : "", minWidth: props.fullInfo ? "500px" : "770px" }}
      onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); props.adminInfo ? setShowFullInfo(!showFullInfo) : setShowFullInfo(true) }}>
      <ISTableTitle title={!props.fullInfo ? !props.toPreorder ? t('lastrecords') : t('useInPreorders') : t('title')}
        sx={{ display: "flex", flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          {props.adminInfo && showFullInfo ? <ArrowDropUpOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> :
            props.adminInfo ? <ArrowDropDownOutlined sx={{ cursor: 'pointer', color: theme.palette.success.dark }} /> : <></>}
        </Stack>
      </ISTableTitle>
      {!props.loading &&
        <Collapse style={{ padding: props.adminInfo ? "10px" : 'inherit' }} in={props.adminInfo ? showFullInfo : true}>
          <TableContainer sx={{ overflowX: 'auto', height: !props.fullInfo && !props.toPreorder ? '275px' : '100%' }}>
            {!props.loading &&
              <Table size="medium" stickyHeader={props.fullInfo}>
                <TableHead >
                  <TableRow selected={true}>
                    <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
                    <ISTableHeaderCell align='center'>{t('number')}</ISTableHeaderCell>
                    {props.fullInfo && <ISTableHeaderCell align='center'>{t('status')}</ISTableHeaderCell>}
                    <ISTableHeaderCell>{t('denomination')}</ISTableHeaderCell>
                    {props.fullInfo && <ISTableHeaderCell align='right'>{t('listPrice')}</ISTableHeaderCell>}
                    <ISTableHeaderCell align='right'>{t('origin')}</ISTableHeaderCell>
                    <ISTableHeaderCell align='right'>{t('balance')}</ISTableHeaderCell>
                    {(props.fullInfo || props.showInPreorder) &&
                      <ISTableHeaderCell >{t('condition')}</ISTableHeaderCell>
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!props.loading && rowsToShow.map((row, index) =>
                    <ISTableRow key={index} color={mapActionToColor(row.action)} sx={{ cursor: (row.action !== 'disabled' || !props.toPreorder) && (row.data.saldo! > 0 || (row.data.saldo! <= 0 && !props.toPreorder)) ? 'pointer' : 'default' }}
                      onClick={(e) => { e.stopPropagation(); (row.action !== 'disabled' || !props.toPreorder) && (row.data.saldo! > 0 || (row.data.saldo! <= 0 && !props.toPreorder)) && props.onClickDetail && props.onClickDetail(row.data.id_acopio) }}>
                      <TableCell align='center' sx={{ borderSpacing: "18px 12px" }}>{moment(row.data.fecha).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                      {/*<TableCell align='center'>{props.fullInfo ? row.data.id_factura : Number(row.data.id_factura.slice( 8 )) }</TableCell>  row.data.numero}</TableCell>*/}
                      <TableCell align='center'>{row.data.numero}</TableCell>
                      {props.fullInfo && <TableCell align='center'>{row.data.estado}</TableCell>}
                      <TableCell style={{ width: props.fullInfo ? '400px' : '120px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.data.descripcion}</TableCell>
                      {props.fullInfo && <ISTableCell align='center'>{row.data.codigo_listadeprecios}</ISTableCell>}
                      <TableCell align='right'>{formattedCurrency(row.data.importe)}</TableCell>
                      <TableCell align='right'>{formattedCurrency(row.data.saldo ? row.data.saldo : 0)}</TableCell>
                      {(props.fullInfo || props.showInPreorder) &&
                        <Tooltip title={row.action === 'disabled' ? props.showInPreorder ? row.statusPO === 'P' ? t('preorder.pendingApproval') : row.statusPO === 'H' ? t('preorder.holded') : row.statusPO === 'N' ? t('preorder.notAvailable') : row.statusPO === 'C' ? t('preorder.approved') : `${t('inUse')} ${row.user}` : `${t('doneBy')} ${row.user}` : ''}>
                          <TableCell style={{ maxWidth: props.fullInfo || props.showInPreorder ? '300px' : '80px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {row.action !== 'disabled' ? row.data.estado === 'FINALIZADO' ? t('preorder.consumed') : (row.data.saldo! > 0) ? t('available') : t('preorder.notAvailable') : props.showInPreorder ? row.statusPO === 'P' ? t('preorder.pendingApproval') : row.statusPO === 'H' ? t('preorder.holded') : row.statusPO === 'N' ? t('preorder.notAvailable') : row.statusPO === 'C' ? t('preorder.approved') : `${t('inUse')} ${row.user}` : t(`preorder.${preorderStatusToDescription(row.statusPO!)}`)}
                          </TableCell>
                        </Tooltip>}
                    </ISTableRow>
                  )}
                </TableBody>
              </Table>}
            {props.fullInfo && <PaginationButtons pagination={props.pagination} changePage={props.onChangePage} />}
          </TableContainer>
        </Collapse>}
      {props.loading &&
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={25} />
        </div>
      }
    </Stack>
  );
}

export default Prepayments


interface DetailProps {
  rows: UsePrepayment[];
  id_factura?: string;
  loading: boolean;
  pagination?: Pagination;
  onChangePage?: (page: number) => void;
  onClickOrderDetail?: (usePrepayment: UsePrepayment) => void;
  onClickInvoiceDetail?: (usePrepayment: UsePrepayment) => void;
}


export const PrepaymentDetails: React.FC<DetailProps> = (props) => {
  const { t } = useTranslation(['prepayment', 'order'])
  return (
    <Stack style={{ display: 'flex', flexDirection: 'column', height: '80%', borderRadius: "10px", margin: "10px", minWidth: "500px" }}>
      <ISTableTitle title={t('titleDetail')} />
      <TableContainer sx={{ overflowX: 'auto', height: '100%' }}>
        <Table size="medium" stickyHeader >
          <TableHead>
            <TableRow selected={true}>
              <ISTableHeaderCell align='center'>{t('date')}</ISTableHeaderCell>
              <ISTableHeaderCell align='center'>{t('useNumber')}</ISTableHeaderCell>
              <ISTableHeaderCell>{t('description')}</ISTableHeaderCell>
              <ISTableHeaderCell></ISTableHeaderCell>
              <ISTableHeaderCell align='right'>{t('total')}</ISTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.loading &&
              props.rows.map((row, index) => {
                return (
                  <ISTableRow color='default' key={index} sx={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      row.codigo_comprobante_utilizacion === 'PD' ? props.onClickOrderDetail && props.onClickOrderDetail(row) :
                        props.onClickInvoiceDetail && props.onClickInvoiceDetail(row)
                    }}>
                    <TableCell align='center' sx={{ borderSpacing: "18px 12px" }}>{moment(row.fecha_comprobante_utilizacion ? row.fecha_comprobante_utilizacion : row.fecha_acopio).tz("America/Argentina/Buenos_Aires").format('DD/MM/YYYY')}</TableCell>
                    <TableCell align='center'>{row.codigo_comprobante_utilizacion ? row.id_utilizacion.includes('C') ? row.id_utilizacion : row.numero_comprobante_utilizacion : props.id_factura ? props.id_factura : row.id_acopio}</TableCell>
                    <TableCell>{row.descripcion_comprobante_utilizacion ? row.descripcion_comprobante_utilizacion : t('origin')}</TableCell>
                    <TableCell>{row.estado ? t(`${statusOrder(row.estado)?.name}`, { ns: 'order' }) : ''} {row.estado && statusOrder(row.estado)?.adic ? t(`${statusOrder(row.estado)?.adic}`, { ns: 'order' }) : ''}</TableCell>
                    <TableCell align='right'>{row.codigo_comprobante_utilizacion ? formattedCurrency(row.importe_comprobante_utilizacion) : formattedCurrency(row.importe)}</TableCell>
                  </ISTableRow>
                )
              })
            }
          </TableBody>
        </Table>
        {props.loading &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
        {<PaginationButtons pagination={props.pagination} />}
      </TableContainer>
    </Stack>
  );
}
